import { COLORS, MINE_EXPLOSION_RADIUS } from "@game/config";
import type { Mine } from "@game/domain";
import { createLogger } from "../logger";
import { dispatchNetworkMessage } from "../networking/network.dispatch";
import { isActiveRealtimeBackendConnected } from "../networking/realtime.runtime";

const logger = createLogger("MINE");

// Track last deployment time for client-side debouncing
let lastClientDeploymentTime = 0;
const CLIENT_DEPLOYMENT_DEBOUNCE_MS = 100; // Prevent rapid double-clicks

export interface MineRenderingOptions {
  isVisible: (x: number, y: number) => boolean;
  getWrappedPosition: (x: number, y: number) => { x: number; y: number };
  playerId?: string;
}

export function renderMines(
  ctx: CanvasRenderingContext2D,
  mines: Mine[],
  players: Map<string, any>,
  options: MineRenderingOptions,
): void {
  if (mines.length === 0) return;

  for (const mine of mines) {
    const visible = options.isVisible(mine.pos.x, mine.pos.y);

    if (visible) {
      const currentTime = Date.now();
      const age = (currentTime - mine.deployTime) / 1000;
      const timeLeft = 4.0 - age;

      // Calculate blink rate (accelerates over time)
      const blinkPeriod = Math.max(0.1, 0.8 * (timeLeft / 4.0)); // From 0.8s to 0.1s
      const blinkPhase = (age % blinkPeriod) / blinkPeriod;
      const opacity = 0.3 + 0.7 * Math.sin(blinkPhase * Math.PI);

      const isMyMine = mine.ownerId === options.playerId;
      const owner = players.get(mine.ownerId);
      const mineColor =
        owner?.color || (isMyMine ? COLORS.PLAYER : COLORS.ENEMY);

      // Get owner's scale for mine scaling (same as bullet scaling)
      const ownerScale = owner?.scale || 1.0;
      const scaledMineRadius = mine.radius * ownerScale;

      const wrapped = options.getWrappedPosition(mine.pos.x, mine.pos.y);

      // Draw explosion radius area first (behind the mine)
      const scaledExplosionRadius = MINE_EXPLOSION_RADIUS * ownerScale;

      ctx.save();
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = "rgba(255, 0, 0, 0.025)"; // Very subtle red tint
      ctx.beginPath();
      ctx.arc(wrapped.x, wrapped.y, scaledExplosionRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = "#ff0000";
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = opacity * 0.4; // Pulses with mine warning
      ctx.beginPath();
      ctx.arc(wrapped.x, wrapped.y, scaledExplosionRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = opacity;

      ctx.strokeStyle = mineColor;
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.arc(wrapped.x, wrapped.y, scaledMineRadius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(wrapped.x, wrapped.y, scaledMineRadius * 0.5, 0, Math.PI * 2);
      ctx.stroke();

      const spikeCount = 8;
      const spikeLength = scaledMineRadius * 0.5;
      for (let i = 0; i < spikeCount; i++) {
        const angle = (i / spikeCount) * Math.PI * 2;
        const spikeX = wrapped.x + Math.cos(angle) * scaledMineRadius;
        const spikeY = wrapped.y + Math.sin(angle) * scaledMineRadius;
        const tipX =
          wrapped.x + Math.cos(angle) * (scaledMineRadius + spikeLength);
        const tipY =
          wrapped.y + Math.sin(angle) * (scaledMineRadius + spikeLength);

        ctx.beginPath();
        ctx.moveTo(spikeX, spikeY);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();
      }

      ctx.restore();

      if (mine.health < mine.maxHealth) {
        ctx.save();

        const barWidth = 30;
        const barHeight = 3;
        const barY = wrapped.y - scaledMineRadius - 10;

        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.fillRect(wrapped.x - barWidth / 2, barY, barWidth, barHeight);

        const healthPct = mine.health / mine.maxHealth;
        ctx.fillStyle =
          healthPct > 0.5
            ? "#00ff00"
            : healthPct > 0.25
              ? "#ffaa00"
              : "#ff0000";
        ctx.fillRect(
          wrapped.x - barWidth / 2,
          barY,
          barWidth * healthPct,
          barHeight,
        );

        ctx.strokeStyle = mineColor;
        ctx.lineWidth = 1;
        ctx.strokeRect(wrapped.x - barWidth / 2, barY, barWidth, barHeight);

        ctx.restore();
      }
    }
  }
}

export function handleMineDeployment(): void {
  const now = Date.now();
  const timeSinceLastDeploy = now - lastClientDeploymentTime;

  // Client-side debouncing to prevent rapid double-clicks
  if (timeSinceLastDeploy < CLIENT_DEPLOYMENT_DEBOUNCE_MS) {
    logger.debug(
      `[MINE] Ignoring rapid deployment attempt (${timeSinceLastDeploy}ms since last)`,
    );
    return;
  }

  logger.debug("Right click detected - attempting mine deployment");
  if (isActiveRealtimeBackendConnected()) {
    lastClientDeploymentTime = now;
    logger.info("Sending mine deployment request to server");
    dispatchNetworkMessage({ t: "deploy_mine" });
  } else {
    logger.warn("Cannot deploy mine - realtime backend not connected", {
      connected: false,
    });
  }
}

export function renderMineCooldownBar(
  ctx: CanvasRenderingContext2D,
  playerX: number,
  playerY: number,
  scaledRadius: number,
  lastMineDeployAt: number,
  mineCooldownSeconds: number = 8.0,
): void {
  ctx.save();

  const barWidth = 40;
  const barHeight = 3;
  const barY = playerY - scaledRadius * 1.5 + 8;

  const now = Date.now();
  const timeSinceLastDeploy = lastMineDeployAt
    ? (now - lastMineDeployAt) / 1000
    : mineCooldownSeconds;
  const cooldownProgress = Math.min(
    timeSinceLastDeploy / mineCooldownSeconds,
    1.0,
  );

  ctx.fillStyle = "rgba(50, 50, 50, 0.7)";
  ctx.fillRect(playerX - barWidth / 2, barY, barWidth, barHeight);

  const progressWidth = barWidth * cooldownProgress;
  if (progressWidth > 0) {
    ctx.fillStyle = "rgba(150, 150, 150, 0.7)";
    ctx.fillRect(playerX - barWidth / 2, barY, progressWidth, barHeight);
  }

  ctx.strokeStyle = "rgba(100, 100, 100, 0.7)";
  ctx.lineWidth = 1;
  ctx.strokeRect(playerX - barWidth / 2, barY, barWidth, barHeight);

  ctx.restore();
}
